/**
 * VRG-545 — what Vera would earn a year brokering this lead's resales.
 *
 * ## The model, in the founder's terms (settled 2026-09-07)
 *
 *     units resold  = units × resaleRate
 *     resale value  = price × resaleValueRatio          (per item, secondhand)
 *     resale GMV    = units resold × resale value       (the market this lead moves)
 *     brokerage ARR = resale GMV × 0.025
 *
 * Worked, on the shape of a real row — 48,000 units, 20% resold, $1,250 retail, 45%
 * resale ratio: 9,600 items × $562.50 = $5,400,000 of resale value, × 0.025 = **$135,000**.
 *
 * ## ⚠️ THE 2.5% IS OF RESALE VALUE, AND IT IS NOT `brokerTake`
 *
 * This is the one thing on this page that is easy to "fix" into being wrong, so it is
 * stated before anything else. There are TWO percentages in play and they belong to
 * different parties:
 *
 * | | whose | what it is |
 * |---|---|---|
 * | `brokerTake` (a slider, default 5%) | the BRAND's | "your share of a verified resale" — what the brand keeps out of a secondhand sale |
 * | `VERA_BROKERAGE_FEE` (2.5%, here) | VERA's | the fee Vera charges the SELLER on the transaction |
 *
 * They are levied on the same base — the resale value — and neither is a share of the
 * other. Taking 2.5% OF the brand's take on the row above gives **$6,750**: twenty times
 * smaller, entirely plausible on screen, and wrong. Nothing would flag it.
 *
 * So `brokerTake` is deliberately NOT read by this module. It stays on the grid as its own
 * column (the visitor's answer to the brand's own question) and is not an input here.
 *
 * ## ⚠️ NOT A FORECAST, and blank on any input that is ours
 *
 * Four of the visitor's numbers go in, and vera-website SEEDS ALL FOUR — units at 25,000,
 * price at 850, resaleRate at 20%, resaleValueRatio at 45%. A row where the visitor touched
 * none of them still arrives carrying every one, and multiplying them produces a large,
 * specific, entirely fabricated figure under a heading reading "ARR".
 *
 * So the gate is ALL FOUR, not the headline one: `isVisitorSupplied` must pass for each, or
 * there is no cell. A figure built from three of their answers and one of our defaults is
 * still partly furniture and the output does not say which part. This is stricter than the
 * subscription column next to it — that one depends on `units` alone — so the two columns
 * legitimately disagree about whether a given row can be priced. That is the gate working,
 * not an inconsistency: they are built from different numbers.
 *
 * ## The currency IS the row's, unlike the column beside it
 *
 * ⚠️ THIS FIGURE IS IN `roi_submission.currency`; `potentialRevenueColumn`'s is in USD. The
 * two revenue columns on this grid are deliberately in DIFFERENT currencies and a reader
 * comparing them across a non-USD row is comparing units, not amounts.
 *
 * The reason is that each figure inherits the currency of what it is derived FROM.
 * Subscription ARR comes out of Vera's own catalog (plan `amount`, the Stripe activation
 * ladder), which carries no currency column and is denominated in USD throughout — so
 * labelling it with a euro lead's own currency would state dollars under a `€`. Brokerage
 * ARR comes out of the visitor's own `price`, typed under a currency they selected on
 * vera-website, so labelling it USD would do the same error in the other direction.
 *
 * Both are the honest label for their own number. Nothing here converts (VRG-577 is the
 * conversion subsystem), and the two tooltips each name their currency so a reader is told
 * rather than left to notice.
 */
import type { SignupEntry } from 'hooks/api/useSignups'

import { WEBSITE_FIELDS, type WebsiteField } from './websiteFields'
import { isVisitorSupplied, websiteSortValue } from './websiteValues'

/**
 * Vera's brokerage fee on a verified resale — 2.5%, charged to the SELLER.
 *
 * Founder's figure, 2026-09-07. Named rather than inlined so the one place it could be
 * confused with `brokerTake` is the one place it is defined; see this module's header for
 * the twenty-fold error that confusion produces.
 */
const VERA_BROKERAGE_FEE = 0.025

/**
 * The four manifest entries this is built from, resolved by id.
 *
 * From `WEBSITE_FIELDS` rather than by reading `roi.units` directly, because the manifest
 * is what carries `websiteDefault` — and the default is the whole basis of the gate. A
 * hand-written field descriptor here would hold its own copy of 25,000 / 850 / 0.2 / 0.45
 * and go stale silently the day vera-website moves one, which is precisely the failure
 * `resaleBuyerConversion` (5% → 28%) already demonstrated on this surface.
 */
const field = (id: string): WebsiteField | undefined =>
  WEBSITE_FIELDS.find(f => f.id === id)

const UNITS_FIELD = field('units')
const PRICE_FIELD = field('price')
const RESALE_RATE_FIELD = field('resaleRate')
const RESALE_VALUE_RATIO_FIELD = field('resaleValueRatio')

export interface BrokerageRevenue {
  /** `units × resaleRate` — items changing hands secondhand in a year. */
  unitsResold: number
  /** `price × resaleValueRatio` — what one of them fetches, in the row's currency. */
  resaleUnitValue: number
  /** The two multiplied — the secondhand market this lead moves, per year. */
  resaleGmv: number
  /** Vera's 2.5% of that. The figure the cell renders. */
  total: number
  /** The rates the arithmetic used, as RATIOS, for the tooltip to show its working. */
  resaleRate: number
  resaleValueRatio: number
}

/**
 * A rate field's value as a RATIO (`0.2`), read through the same function the grid's own
 * rate columns sort on.
 *
 * ⚠️ `websiteSortValue` returns a rate as a PERCENT (`20`), deliberately — its docblock
 * explains that display and filter must read one number. So this divides by 100, and that
 * step is load-bearing: skipping it multiplies the output by 10,000 (two rates, each 100x).
 *
 * Routed through `websiteSortValue` rather than reading `roi.assumptions[id]` directly so
 * that this figure and the rate column beside it cannot disagree — that function is what
 * resolves the nesting AND `assumptionsUnit`, the two traps `websiteValues.ts` records as
 * having already produced a wrong render here.
 */
const ratio = (row: SignupEntry, f: WebsiteField): number | null => {
  const percent = websiteSortValue(row, f)
  return typeof percent === 'number' && Number.isFinite(percent) ? percent / 100 : null
}

const positiveNumber = (row: SignupEntry, f: WebsiteField): number | null => {
  const value = websiteSortValue(row, f)
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null
}

/**
 * Price a lead's resale flow. `null` whenever any of the four inputs is ours, missing or
 * unusable — each a deliberate refusal rather than an omission. See the module header.
 */
export const brokerageRevenue = (row: SignupEntry): BrokerageRevenue | null => {
  if (!row?.roi_submission) return null
  if (!UNITS_FIELD || !PRICE_FIELD || !RESALE_RATE_FIELD || !RESALE_VALUE_RATIO_FIELD) {
    // A manifest id was renamed out from under this module. Refusing is the only honest
    // answer — silently dropping one factor would scale the output rather than blank it.
    return null
  }

  const inputs = [UNITS_FIELD, PRICE_FIELD, RESALE_RATE_FIELD, RESALE_VALUE_RATIO_FIELD]
  if (!inputs.every(f => isVisitorSupplied(row, f))) return null

  const units = positiveNumber(row, UNITS_FIELD)
  const price = positiveNumber(row, PRICE_FIELD)
  const resaleRate = ratio(row, RESALE_RATE_FIELD)
  const resaleValueRatio = ratio(row, RESALE_VALUE_RATIO_FIELD)

  if (units === null || price === null) return null
  // A rate of exactly 0 is a real answer ("nothing is resold") and it prices to zero, not
  // to "unknown" — but zero brokerage revenue is not a lead value worth a column slot, and
  // rendering `$0` would sort it among the priced rows as the cheapest lead on the page.
  // Same reasoning as `potentialSubscriptionRevenue` returning null rather than 0.
  if (!resaleRate || !resaleValueRatio) return null
  if (resaleRate < 0 || resaleValueRatio < 0) return null

  const unitsResold = units * resaleRate
  const resaleUnitValue = price * resaleValueRatio
  const resaleGmv = unitsResold * resaleUnitValue

  return {
    unitsResold,
    resaleUnitValue,
    resaleGmv,
    total: resaleGmv * VERA_BROKERAGE_FEE,
    resaleRate,
    resaleValueRatio,
  }
}

/** The fee, for the tooltip to state rather than the cell to imply. */
export const BROKERAGE_FEE_PERCENT = VERA_BROKERAGE_FEE * 100
